/**
 * Base Analytics Provider Interface
 * All analytics providers should extend this class
 */
export class AnalyticsProvider {
  constructor(name) {
    this.name = name;
    this.config = null;
    this.propertyContext = null;
    this.isInitialized = false;
  }

  /**
   * Initialize the provider with configuration
   * @param {Object} config - Provider-specific configuration
   * @param {Object} propertyContext - Property context data
   */
  initialize(config, propertyContext) {
    this.config = config;
    this.propertyContext = propertyContext;
    this.isInitialized = true;

    console.log(`[${this.name}] Provider initialized with config:`, config);
  }

  /**
   * Track an event - must be implemented by provider
   * @param {string} eventName - Name of the event
   * @param {Object} eventData - Event data
   */
  track(eventName, eventData) {
    throw new Error(`Provider ${this.name} must implement track() method`);
  }

  /**
   * Check if provider is ready to track events
   */
  isReady() {
    return this.isInitialized && this.config && this.config.enabled && this.hasConsent();
  }

  /**
   * Check if user has given consent for this provider type
   */
  hasConsent() {
    if (typeof window === 'undefined') return true; // SSR - assume consent

    const consent = window.analyticsConsent;
    if (!consent) return false; // No consent data available

    // Map provider types to consent categories
    const providerType = this.getProviderType();

    switch (providerType) {
      case 'analytics':
        return consent.analytics;
      case 'marketing':
        return consent.marketing;
      case 'experience':
        return consent.experience;
      case 'technical':
        return consent.technical;
      default:
        return false;
    }
  }

  /**
   * Get provider type for consent checking
   * Override in specific providers
   */
  getProviderType() {
    return 'analytics'; // Default to analytics
  }

  /**
   * Map standard e-commerce events to provider-specific events
   */
  mapEvent(standardEvent) {
    // Default mapping - providers can override
    const eventMap = {
      'page_view': 'PageView',
      'view_item': 'ViewContent',
      'add_to_cart': 'AddToCart',
      'remove_from_cart': 'RemoveFromCart',
      'begin_checkout': 'InitiateCheckout',
      'add_payment_info': 'AddPaymentInfo',
      'purchase': 'Purchase',
      'search': 'Search',
      'view_item_list': 'ViewCategory'
    };

    return eventMap[standardEvent] || standardEvent;
  }

  /**
   * Extract e-commerce data from event
   */
  extractEcommerceData(eventData) {
    return eventData.ecommerce || {};
  }

  /**
   * Log provider-specific messages
   */
  log(message, data = null) {
    if (data) {
      console.log(`[${this.name}]`, message, data);
    } else {
      console.log(`[${this.name}]`, message);
    }
  }

  /**
   * Log provider-specific errors
   */
  error(message, error = null) {
    if (error) {
      console.error(`[${this.name}]`, message, error);
    } else {
      console.error(`[${this.name}]`, message);
    }
  }
}